import * as React from 'react';
import { useStyles } from './TaskSidebar.styles';
import { Box, Link, Typography } from '@material-ui/core';
import {
  BulkAdjustmentTriggerContext,
  CategorizeClientTransactionsTriggerContext,
  CollectAllLedgerStatementsJobParameters,
  CollectAllLedgerStatementsTriggerContext,
  CollectMonthlyStatementJobParameters,
  JobType,
  ResolveBalanceCheckerJobParameters,
  ResolveBulkAdjustmentJobParameters,
  ResolveDocumentAdjustmentJobParameters,
  ResolveIncorrectAdjustmentJobParameters,
  ResolveIncorrectAdjustmentTriggerContext,
  ResolveIncorrectBalanceJobParameters,
  ResolveIncorrectBooksJobParameters,
  ResolveIncorrectBooksTriggerContext,
  ResolveIncorrectCategorizationJobParameters,
  ResolveIncorrectCategorizationTriggerContext,
  ResolveThirdPartyLoginAdjustmentJobParameters,
  ReviewGeneralLedgerTriggerContext,
  SetupNewConnectionTriggerContext,
  Task,
  TriageClientDocumentsTriggerContext,
} from 'task-manager/types';
import { differenceInCalendarMonths, format, isSameMonth, isSameYear, parse } from 'date-fns';
import formatCurrency from 'format-currency/formatCurrency';
import moment from 'moment';
import { TaskSidebarAnalytics } from './taskSidebarAnalytics';
import { useTaskManagerContext } from '../TaskManagerContext';

export const TaskDetail = ({ label, content }: { label: string; content: React.ReactElement }) => {
  const classes = useStyles();
  return (
    <Box className={classes.taskDetail}>
      <Typography variant="body1">
        <strong>{label}</strong>
      </Typography>
      {content}
    </Box>
  );
};

const ADDON_NOT_FOUND = '[AddOn Not Found]';
const LEDGER_NOT_FOUND = '[Ledger Not Found]';

export type JobDetailsBuilder = (task: Task) => React.ReactElement | null;

export const taskDetailsLookup: { [key in JobType]: JobDetailsBuilder } = {
  [JobType.RESOLVE_BALANCE_CHECKER]: task => {
    const { loggedInPerson } = useTaskManagerContext();
    return (
      <TaskDetail
        label="Ledger Account"
        content={
          <Link
            href={`/app/${task.job.client.id}/ledger-accounts`}
            target="_blank"
            onClick={() => trackClick('Ledger Account', task, loggedInPerson.id)}
          >
            {(task.job.parameters as ResolveBalanceCheckerJobParameters)?.ledgerAccount?.name ??
              LEDGER_NOT_FOUND}
          </Link>
        }
      />
    );
  },
  [JobType.RESOLVE_PLAID_CONNECTION]: task => {
    const { loggedInPerson } = useTaskManagerContext();
    return (
      <TaskDetail
        label="Ledger Account"
        content={
          <Link
            href={`/app/${task.job.client.id}/ledger-accounts`}
            target="_blank"
            onClick={() => trackClick('Ledger Account', task, loggedInPerson.id)}
          >
            {(task.job.parameters as ResolveBalanceCheckerJobParameters)?.ledgerAccount?.name ??
              LEDGER_NOT_FOUND}
          </Link>
        }
      />
    );
  },
  [JobType.RESOLVE_DOCUMENT_ADJUSTMENT]: task => {
    const { loggedInPerson } = useTaskManagerContext();
    const { addOn, document } = task.job.parameters as ResolveDocumentAdjustmentJobParameters;
    const addOnName = addOn?.name ?? ADDON_NOT_FOUND;
    const documentName = document?.name;
    const documentDate = document?.date;
    return (
      <>
        {documentDate ? (
          <TaskDetail
            label="Document Date"
            content={<Typography variant="body1">{documentDate?.toString()}</Typography>}
          />
        ) : null}
        <TaskDetail
          label="AddOn"
          content={
            <Link
              href={`/app/${task.job.client.id}/ledger-accounts?tab=ADDONS&addOnId=${addOn?.id}`}
              target="_blank"
              onClick={() => trackClick('AddOn', task, loggedInPerson.id)}
            >
              {addOnName}
            </Link>
          }
        />
        {documentName ? (
          <TaskDetail
            label="Document Name"
            content={<Typography variant="body1">{documentName}</Typography>}
          />
        ) : null}
      </>
    );
  },
  [JobType.RESOLVE_3RD_PARTY_LOGIN_ADJUSTMENT]: task => {
    const { loggedInPerson } = useTaskManagerContext();
    const { yearMonth, addOn } = task.job
      .parameters as ResolveThirdPartyLoginAdjustmentJobParameters;
    return (
      <>
        <TaskDetail
          label="Scope to Collect"
          content={<Typography variant="body1">{yearMonth}</Typography>}
        />
        {addOn ? (
          <TaskDetail
            label="AddOn"
            content={
              <Link
                href={`/app/${task.job.client.id}/ledger-accounts?tab=ADDONS&addOnId=${addOn?.id}`}
                target="_blank"
                onClick={() => trackClick('AddOn', task, loggedInPerson.id)}
              >
                {addOn?.name ?? ADDON_NOT_FOUND}
              </Link>
            }
          />
        ) : null}
      </>
    );
  },
  [JobType.RESOLVE_BULK_ADJUSTMENT]: task => {
    const { loggedInPerson } = useTaskManagerContext();
    const { addOn } = task.job.parameters as ResolveBulkAdjustmentJobParameters;
    const triggerContext = task.job.triggerContext as BulkAdjustmentTriggerContext;
    return (
      <>
        <TaskDetail
          label="Date Range"
          content={<Typography variant="body1">{formatDateRange(triggerContext)}</Typography>}
        />
        <TaskDetail
          label="AddOn"
          content={
            <Link
              href={`/app/${task.job.client.id}/ledger-accounts?tab=ADDONS&addOnId=${addOn?.id}`}
              target="_blank"
              onClick={() => trackClick('AddOn', task, loggedInPerson.id)}
            >
              {addOn?.name ?? ADDON_NOT_FOUND}
            </Link>
          }
        />
      </>
    );
  },
  [JobType.CATEGORIZE_CLIENT_TRANSACTIONS]: task => {
    const triggerContext = task.job.triggerContext as CategorizeClientTransactionsTriggerContext;
    let oldestTransactionText = '';
    if (triggerContext?.oldestTransactionDate) {
      const oldestDateStr = triggerContext?.oldestTransactionDate;
      const oldestDate = new Date(oldestDateStr);
      const daysOld = moment().diff(oldestDate, 'days');
      const oldestTransaction = moment(oldestDate).utc().format('MMMM YYYY');
      oldestTransactionText = `${oldestTransaction} - ${daysOld} ${
        daysOld === 1 ? 'day' : 'days'
      } old`;
    }
    return (
      <>
        <TaskDetail
          label="Notes Received"
          content={<Typography variant="body1">{triggerContext?.numNeedsReview}</Typography>}
        />
        <TaskDetail
          label="Oldest Transaction Date"
          content={<Typography variant="body1">{oldestTransactionText}</Typography>}
        />
        <TaskDetail
          label="Approximate Number in ACE/R"
          content={<Typography variant="body1">{triggerContext?.numAwaitingCategory}</Typography>}
        />
        {triggerContext?.totalAmountAwaitingCategory ? (
          <TaskDetail
            label="ACE/R Total"
            content={
              <Typography variant="body1">
                {formatCurrency(triggerContext?.totalAmountAwaitingCategory)}
              </Typography>
            }
          />
        ) : null}
      </>
    );
  },
  [JobType.TRIAGE_CLIENT_DOCUMENTS]: task => {
    const triggerContext = task.job.triggerContext as TriageClientDocumentsTriggerContext;
    return (
      <TaskDetail
        label="Approximate Number of Unknown Docs"
        content={<Typography variant="body1">{triggerContext?.numUnknownDocs}</Typography>}
      />
    );
  },
  [JobType.COLLECT_MONTHLY_STATEMENT]: task => {
    const { loggedInPerson } = useTaskManagerContext();
    return (
      <>
        <TaskDetail
          label="Ledger Account"
          content={
            <Link
              href={`/app/${task.job.client.id}/ledger-accounts`}
              target="_blank"
              onClick={() => trackClick('Ledger Account', task, loggedInPerson.id)}
            >
              {(task.job.parameters as CollectMonthlyStatementJobParameters)?.ledgerAccount?.name ??
                LEDGER_NOT_FOUND}
            </Link>
          }
        />
        <TaskDetail
          label="Statement Month"
          content={
            <Typography variant="body1">
              {(task.job.parameters as CollectMonthlyStatementJobParameters)?.statementMonth}
            </Typography>
          }
        />
      </>
    );
  },
  [JobType.COLLECT_ALL_LEDGER_STATEMENTS]: task => {
    const { loggedInPerson } = useTaskManagerContext();
    const params = task.job.parameters as CollectAllLedgerStatementsJobParameters;
    const triggerContext = task.job.triggerContext as CollectAllLedgerStatementsTriggerContext;
    return (
      <>
        <TaskDetail
          label="Ledger Account"
          content={
            <Link
              href={`/app/${task.job.client.id}/ledger-accounts`}
              target="_blank"
              onClick={() => trackClick('Ledger Account', task, loggedInPerson.id)}
            >
              {params?.ledgerAccount?.name ?? LEDGER_NOT_FOUND}
            </Link>
          }
        />
        <TaskDetail
          label="Active Months"
          content={
            <Typography variant="body1">
              {formatDateActiveAsNumMonths(triggerContext?.ledgerDateActive)}
            </Typography>
          }
        />
      </>
    );
  },
  [JobType.REVIEW_GENERAL_LEDGER]: task => {
    const triggerContext = task.job.triggerContext as ReviewGeneralLedgerTriggerContext;
    return (
      <>
        <TaskDetail
          label="Start Date"
          content={<Typography variant="body1">{triggerContext?.startDate}</Typography>}
        />
        <TaskDetail
          label="End Date"
          content={<Typography variant="body1">{triggerContext?.endDate}</Typography>}
        />
      </>
    );
  },
  [JobType.RESOLVE_INCORRECT_ADJUSTMENT]: task => {
    const params = task.job.parameters as ResolveIncorrectAdjustmentJobParameters;
    const triggerContext = task.job.triggerContext as ResolveIncorrectAdjustmentTriggerContext;
    if (!triggerContext?.startDate) {
      return (
        <TaskDetail
          label="Review Period"
          content={<Typography variant="body1">{params?.reviewPeriod}</Typography>}
        />
      );
    }
    return (
      <>
        <TaskDetail
          label="Start Date"
          content={<Typography variant="body1">{triggerContext?.startDate}</Typography>}
        />
        <TaskDetail
          label="End Date"
          content={<Typography variant="body1">{triggerContext?.endDate}</Typography>}
        />
        <TaskDetail
          label="# Months"
          content={<Typography variant="body1">{triggerContext.numMonths}</Typography>}
        />
      </>
    );
  },
  [JobType.RESOLVE_INCORRECT_BALANCE]: task => {
    const params = task.job.parameters as ResolveIncorrectBalanceJobParameters;
    const triggerContext = task.job.triggerContext as ResolveIncorrectAdjustmentTriggerContext;
    if (!triggerContext?.startDate) {
      return (
        <TaskDetail
          label="Review Period"
          content={<Typography variant="body1">{params?.reviewPeriod}</Typography>}
        />
      );
    }
    return (
      <>
        <TaskDetail
          label="Start Date"
          content={<Typography variant="body1">{triggerContext?.startDate}</Typography>}
        />
        <TaskDetail
          label="End Date"
          content={<Typography variant="body1">{triggerContext?.endDate}</Typography>}
        />
        <TaskDetail
          label="# Months"
          content={<Typography variant="body1">{triggerContext.numMonths}</Typography>}
        />
      </>
    );
  },
  [JobType.RESOLVE_INCORRECT_CATEGORIZATION]: task => {
    const params = task.job.parameters as ResolveIncorrectCategorizationJobParameters;
    const triggerContext = task.job.triggerContext as ResolveIncorrectCategorizationTriggerContext;
    if (!triggerContext?.startDate) {
      return (
        <TaskDetail
          label="Review Period"
          content={<Typography variant="body1">{params?.reviewPeriod}</Typography>}
        />
      );
    }
    return (
      <>
        <TaskDetail
          label="Start Date"
          content={<Typography variant="body1">{triggerContext?.startDate}</Typography>}
        />
        <TaskDetail
          label="End Date"
          content={<Typography variant="body1">{triggerContext?.endDate}</Typography>}
        />
        <TaskDetail
          label="# Months"
          content={<Typography variant="body1">{triggerContext.numMonths}</Typography>}
        />
      </>
    );
  },
  [JobType.RESOLVE_INCORRECT_BOOKS]: task => {
    const params = task.job.parameters as ResolveIncorrectBooksJobParameters;
    const triggerContext = task.job.triggerContext as ResolveIncorrectBooksTriggerContext;
    if (!triggerContext?.startDate) {
      return (
        <TaskDetail
          label="Review Period"
          content={<Typography variant="body1">{params?.reviewPeriod}</Typography>}
        />
      );
    }
    return (
      <>
        <TaskDetail
          label="Start Date"
          content={<Typography variant="body1">{triggerContext?.startDate}</Typography>}
        />
        <TaskDetail
          label="End Date"
          content={<Typography variant="body1">{triggerContext?.endDate}</Typography>}
        />
        <TaskDetail
          label="# Months"
          content={<Typography variant="body1">{triggerContext.numMonths}</Typography>}
        />
      </>
    );
  },
  [JobType.SETUP_NEW_CONNECTION]: task => {
    const { loggedInPerson } = useTaskManagerContext();
    return (
      <>
        <TaskDetail
          label="Source"
          content={
            <Link
              href={`/app/${task.job.client.id}/new-accounts`}
              target="_blank"
              onClick={() => trackClick('Source', task, loggedInPerson.id)}
            >
              {(task.job.triggerContext as SetupNewConnectionTriggerContext)?.source ?? 'Unknown'}
            </Link>
          }
        />
        <TaskDetail
          label="Institution"
          content={
            <Typography variant="body1">
              {(task.job.triggerContext as SetupNewConnectionTriggerContext)?.institution ??
                'Unknown'}
            </Typography>
          }
        />
        <TaskDetail
          label="# Accounts"
          content={
            <Typography variant="body1">
              {(task.job.triggerContext as SetupNewConnectionTriggerContext)?.numAccounts ?? 0}
            </Typography>
          }
        />
      </>
    );
  },
};

function formatDateActiveAsNumMonths(ledgerDateActive: string): string {
  const date = new Date(ledgerDateActive);
  const now = new Date();
  const diff = differenceInCalendarMonths(now, date);
  return `${diff} months`;
}

function formatDateRange(triggerContext: {
  startDate: string;
  endDate: string;
  numMonthsAdjustmentRequired: number;
}): string {
  if (!triggerContext?.startDate || !triggerContext?.endDate) return '';
  const start = parse(triggerContext.startDate);
  const end = parse(triggerContext.endDate);
  const sameYear = isSameYear(start, end);
  const sameMonth = isSameMonth(start, end);
  const startString = format(start, sameYear ? 'MMM' : 'MMM YYYY');
  const endString = format(end, 'MMM YYYY');
  return `${sameMonth ? '' : `${startString} - `}${endString} (${
    triggerContext.numMonthsAdjustmentRequired
  } months)`;
}

const trackClick = (type: string, task: Task, personId?: string) => {
  const eventName = `Task Sidebar Details - ${type} Clicked`;
  TaskSidebarAnalytics.sendHeapEvent(eventName, task, personId);
};
